/**
 * <product-jewelry-care>
 * PDP "Shipping & returns" + "Jewelry care" accordion group.
 *
 * - Variant switching: update(variant) rebuilds the jewelry-care copy from the
 *   related product's materials, mirroring the server-side Liquid in
 *   product-jewelry-care-info-box.liquid.
 * - Public API:
 *     update(variant)
 */

// Special "3+ stones" fallback care entry (metaobject id), mirroring the Liquid.
const NATURE_STONE_JEWELRY_CARE = 199340654658;

class ProductJewelryCare extends HTMLElement {
  connectedCallback() {
    if (this.closest('.product-details-new-2610')) {
      this._layoutQuery = window.matchMedia('(min-width: 768px)');
      const items = Array.from(this.querySelectorAll('.link-item'));
      const desktopStates = items.map((item) => item.classList.contains('active'));
      this._layoutChangedHandler = () => {
        items.forEach((item, index) => {
          item.classList.toggle('active', !this._layoutQuery.matches || desktopStates[index]);
        });
      };
      this._layoutQuery.addEventListener('change', this._layoutChangedHandler);
      this._layoutChangedHandler();
    }
    const link = this.querySelector('.link-item a[href$="/pages/jewelry-care"]');
    this.container = link?.parentElement?.querySelector(".link-content .sub-block") || null;
    this._variantChangedHandler = (event) => {
      const variant = event?.detail?.variant;
      if (variant) this.update(variant);
    };
    window.addEventListener("enroute:variant-changed", this._variantChangedHandler);
  }

  disconnectedCallback() {
    this._layoutQuery?.removeEventListener('change', this._layoutChangedHandler);
    window.removeEventListener("enroute:variant-changed", this._variantChangedHandler);
  }

  /**
   * Rebuild the jewelry-care copy for `variant`. Driven by the variant switcher's
   * enroute:variant-changed event; also callable directly.
   *
   * @param {object} variant - a variant entry from product-info-json
   */
  update(variant) {
    if (!this.container) return;

    const info = this.productInfo();
    const relatedKey = variant?.related_product_key;
    const related = relatedKey ? info?.related_products?.[relatedKey] : null;
    const materials = (related?.product_materials?.length ? related.product_materials : info?.product_materials) || [];
    const careEntries = this.careEntries();
    if (!materials.length || !careEntries.length) return;

    const materialIds = new Set(
      materials
        .map((material) => this.normalizeId(material?.id || material?.system?.id))
        .filter(Boolean)
    );
    const processedCareIds = new Set();
    const matchedStoneMaterials = new Set();
    const metalDescriptions = [];
    const stoneDescriptions = [];

    for (const entry of careEntries) {
      const entryId = this.normalizeId(entry?.id);
      if (!entryId || processedCareIds.has(entryId)) continue;
      processedCareIds.add(entryId);
      const matchedMaterialId = this.matchMaterialId(entry, materialIds);
      if (!matchedMaterialId) continue;

      const type = (entry.type || "").toLowerCase().trim();
      const desc = entry.description || "";
      if (!desc) continue;

      if (type === "stone") {
        matchedStoneMaterials.add(matchedMaterialId);
        stoneDescriptions.push(desc);
      } else {
        metalDescriptions.push(desc);
      }
    }

    const specialStone = careEntries.find((entry) => entry && entry.id === NATURE_STONE_JEWELRY_CARE);
    const stoneOutput =
      matchedStoneMaterials.size >= 3
        ? specialStone?.description || ""
        : stoneDescriptions.join("<br>");
    const metalOutput = metalDescriptions.join("<br>");
    const html = [metalOutput, stoneOutput].filter(Boolean).join("<br>");
    if (html) this.container.innerHTML = html;
  }

  normalizeId(value) {
    return value === null || value === undefined ? null : String(value);
  }

  matchMaterialId(entry, materialIds) {
    const relatedIds = Array.isArray(entry?.relatedIds) ? entry.relatedIds : [];
    for (const relatedId of relatedIds) {
      const id = this.normalizeId(relatedId);
      if (id && materialIds.has(id)) return id;
    }
    return null;
  }

  productInfo() {
    if (this.productInfoCache === undefined) {
      const scope = this.closest("section.product.wp.new-look") || document;
      this.productInfoCache = window._erj_?.getProductInfo?.(scope) || null;
    }
    return this.productInfoCache;
  }

  /**
   * Shop-wide jewelry-care metaobject entries, emitted once per PDP as a global
   * JSON island by `jewelry-care-entries.liquid`.
   *
   * @returns {Array<object>}
   */
  careEntries() {
    if (this.careEntriesCache === undefined) {
      const el = document.getElementById("jewelry-care-entries");
      const text = (el?.textContent || "").trim();
      try {
        this.careEntriesCache = text ? JSON.parse(text) : [];
      } catch (err) {
        console.error("[product-jewelry-care] care entries JSON parse failed:", err);
        this.careEntriesCache = [];
      }
    }
    return this.careEntriesCache;
  }
}

if (!window.customElements.get("product-jewelry-care")) {
  window.customElements.define("product-jewelry-care", ProductJewelryCare);
}
